import React, { useEffect, useState } from 'react';
import api from '../../lib/api';

/**
 * ProjectOverview — high-level rollup. Counts + last deploy + recent memory.
 */
export default function ProjectOverview({ env }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get('/lab/overview').then(({ data }) => setData(data)).catch(() => {});
  }, []);

  if (!data) return <Loading label="loading overview" />;

  const c = data.counts || {};
  const tiles = [
    { label: 'Backend Modules',     v: c.backend_modules },
    { label: 'API Routes',          v: c.backend_routes },
    { label: 'Frontend Pages',      v: c.frontend_pages },
    { label: 'Frontend Components', v: c.frontend_components },
    { label: 'DB Collections',      v: c.db_collections },
    { label: 'Engines',             v: c.engines },
    { label: 'Indexed Features',    v: c.features },
  ];

  return (
    <div data-testid="lab-overview" className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2">
        {tiles.map((t) => (
          <div
            key={t.label}
            data-testid={`lab-overview-tile-${t.label.toLowerCase().replace(/\s+/g, '-')}`}
            className="border border-[var(--border-default)] p-3"
          >
            <div className="text-[9px] tracking-[0.25em] uppercase text-[var(--txt-secondary)] mb-1">
              {t.label}
            </div>
            <div className="text-2xl font-mono tabular text-cyan-sys">{t.v ?? '—'}</div>
          </div>
        ))}
      </div>

      <div className="grid md:grid-cols-2 gap-3">
        <div className="border border-[var(--border-default)] p-3">
          <div className="text-[10px] tracking-[0.25em] uppercase text-cyan-sys mb-2">
            // last deployment
          </div>
          {data.last_deploy ? (
            <div className="text-[11px] font-mono space-y-1">
              <Row k="sha"        v={data.last_deploy.sha} />
              <Row k="env"        v={data.last_deploy.env} />
              <Row k="status"     v={data.last_deploy.status} />
              <Row k="deployed"   v={short(data.last_deploy.deployed_at)} />
              <Row k="build"      v={short(data.last_deploy.build_time)} />
              <Row k="by"         v={data.last_deploy.author} />
            </div>
          ) : (
            <div className="text-[11px] text-[var(--txt-secondary)]">
              No deploys recorded yet.
              {env?.write_enabled === false &&
                ' Production deploys will populate this after the next CI/CD run.'}
            </div>
          )}
        </div>

        <div className="border border-[var(--border-default)] p-3">
          <div className="text-[10px] tracking-[0.25em] uppercase text-cyan-sys mb-2">
            // recent engineering memory
          </div>
          {(data.recent_memory || []).length === 0 ? (
            <div className="text-[11px] text-[var(--txt-secondary)]">No memory entries yet.</div>
          ) : (
            <ul className="text-[11px] space-y-1.5">
              {data.recent_memory.map((m) => (
                <li key={m.id} className="border-l-2 border-cyan-sys pl-2">
                  <div className="text-[var(--txt-primary)]">{m.title}</div>
                  <div className="text-[9px] tracking-[0.2em] uppercase text-[var(--txt-secondary)]">
                    {m.kind} · {short(m.timestamp)}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      <div className="border border-[var(--border-default)] p-3">
        <div className="text-[10px] tracking-[0.25em] uppercase text-cyan-sys mb-2">
          // future-phase capabilities
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-2 text-[11px]">
          {(env?.capabilities || []).map((c) => (
            <div
              key={c.name}
              data-testid={`lab-capability-${c.name}`}
              className={`border p-2 ${c.available ? 'border-buy' : 'border-[var(--border-default)]'}`}
            >
              <div className="flex items-center justify-between">
                <span className="text-[var(--txt-primary)] font-mono">{c.name}</span>
                <span className={`text-[9px] tracking-[0.2em] uppercase ${c.available ? 'text-buy' : 'text-[var(--txt-muted)]'}`}>
                  {c.available ? 'AVAILABLE' : 'COMING SOON'}
                </span>
              </div>
              <div className="text-[10px] text-[var(--txt-secondary)] mt-0.5">{c.description}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function Row({ k, v }) {
  return (
    <div className="flex justify-between gap-3">
      <span className="text-[var(--txt-secondary)] uppercase tracking-wider text-[10px]">{k}</span>
      <span className="tabular text-[var(--txt-primary)]">{String(v ?? '—')}</span>
    </div>
  );
}

function Loading({ label }) {
  return (
    <div className="border border-[var(--border-default)] p-6 text-center text-[11px] text-[var(--txt-secondary)] tracking-wider">
      // {label}…
    </div>
  );
}

function short(iso) {
  if (!iso) return '—';
  return String(iso).replace('T', ' ').slice(0, 19) + 'Z';
}
